import { useMemo, useState, useCallback } from "react";

import {
  Box,
  Button,
  Center,
  Loader,
  ScrollArea,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";
import { BottomModalSection } from "~/../../base-components/lib/modal/ModalSection";
import { networkByNameLookup } from "~/../../shared-features/lib/utils/blockExplorer";

import {
  Condition,
  ConditionType,
  ContractNft,
  NetworkType,
  WalletNft,
} from "apollo/graphql.generated";
import { useAuth } from "~/features/auth/context";
import {
  useContractDetailsQuery,
  useWalletNftsQuery,
} from "~/features/gates/queries/gates.graphql.generated";

import ContractCollectionAttributeGrid, {
  TokenGridAttribute,
} from "../../grids/ContractCollectionAttributeGrid";
import { ContractCollectionTokenGrid } from "../../grids/ContractCollectionTokenGrid";
import {
  SimpleInfiniteGrid,
  SimpleInfiniteListItem,
} from "../../grids/SimpleInfiniteGrid";
import CollectionDetailsSection from "../sections/CollectionDetailsSection";

export type SelectedWalletCondition = {
  network?: NetworkType;
  contract?: string;
  token?: string;
  condition?: Condition;
};

function LoadingElement() {
  return (
    <Center p={"lg"} h={300}>
      <Loader size={32} />
    </Center>
  );
}

function NoResultsElement({ text }: { text?: string }) {
  return (
    <Stack p="lg" align="center">
      <Text size={"lg"}>{text || "No results."}</Text>
    </Stack>
  );
}

export default function WalletTab(props: {
  onSelected: (item?: SelectedWalletCondition) => void;
  mode?: "COLLECTION" | "TOKEN" | "ATTRIBUTE";
}) {
  const { mode = "COLLECTION", onSelected } = props;

  return (
    <Box>
      <Box mih={300}>
        {mode === "COLLECTION" && (
          <WalletCollectionSelectionFlow onSelectCollection={onSelected} />
        )}
        {mode === "TOKEN" && (
          <WalletTokenSelectionFlow onSelectCollectionToken={onSelected} />
        )}
        {mode === "ATTRIBUTE" && (
          <WalletAttributeSelectionFlow
            onSelectCollectionAttribute={onSelected}
          />
        )}
      </Box>
    </Box>
  );
}

function WalletCollectionSelectionFlow(props: {
  onSelectCollection: (data: SelectedWalletCondition) => void;
}) {
  const { onSelectCollection } = props;
  const { isMedium } = useMantineMediaQueries();
  const [selectedToken, setSelectedToken] =
    useState<SimpleInfiniteListItem<WalletNft>>();
  const {
    walletNfts,
    loadMore,
    hasMore,
    loading: loadingWalletNfts,
  } = usePaginatedWalletNfts();

  const collectionNetwork = useMemo(() => {
    let network = NetworkType.ETHEREUM;

    if (selectedToken?.data?.chain === "polygon") {
      network = NetworkType.POLYGON;
    }
    return network;
  }, [selectedToken]);

  const collectionAddress = useMemo(() => {
    return selectedToken?.data?.contract || "";
  }, [selectedToken]);

  const { data: collectionDetails, loading: loadingCollection } =
    useContractDetailsQuery({
      variables: {
        network: collectionNetwork,
        address: collectionAddress,
      },
      skip: selectedToken?.data == null,
    });

  const mappedWalletNfts = useMemo<SimpleInfiniteListItem<WalletNft>[]>(() => {
    if (walletNfts == null) return [];

    return walletNfts.map((nft) => {
      const networkDetails = networkByNameLookup(
        nft.chain.toUpperCase() as NetworkType
      );
      return {
        key: `${nft.contract}-${nft.id}`,
        id: nft.id,
        title: nft.name,
        subtitle: networkDetails.displayName,
        imageUrl: nft.imageUrl ?? "",
        data: nft,
      };
    });
  }, [walletNfts]);

  const handleSelectCollection = () => {
    if (collectionDetails?.contractDetails == null) return;

    const condition: Condition = {
      type: ConditionType.NFT,
      amount: 1,
      data: {
        network: collectionNetwork,
        address: collectionAddress,
        name: collectionDetails.contractDetails.name ?? "",
        collectionName: collectionDetails.contractDetails.name ?? "",
        imageUrl: collectionDetails.contractDetails.imageUrl,
        standard: collectionDetails.contractDetails.standard,
      },
    };

    onSelectCollection({
      network: collectionNetwork,
      contract: collectionAddress,
      token: undefined,
      condition,
    });
  };

  return (
    <Box>
      <ScrollArea.Autosize mih={100} maxHeight={"auto"}>
        <SimpleInfiniteGrid<WalletNft>
          items={mappedWalletNfts}
          hasMore={hasMore}
          loading={loadingWalletNfts}
          loadMore={loadMore}
          selectedItem={selectedToken}
          onSelectItem={setSelectedToken}
          loadingElement={<LoadingElement />}
          emptyResultsElement={
            <NoResultsElement text="No collections found in your wallet." />
          }
          gridCols={isMedium ? 3 : 1}
        />
      </ScrollArea.Autosize>

      <Space h="sm" />

      <BottomModalSection>
        <Button
          disabled={collectionDetails?.contractDetails == null}
          size="xl"
          fullWidth
          loading={loadingCollection}
          onClick={handleSelectCollection}
        >
          Select collection
        </Button>
      </BottomModalSection>
    </Box>
  );
}

function WalletTokenSelectionFlow(props: {
  onSelectCollectionToken: (data: SelectedWalletCondition) => void;
}) {
  const { onSelectCollectionToken } = props;
  const { isMedium } = useMantineMediaQueries();
  const [step, setStep] = useState(0);
  const [selectedFakeCollectionToken, setSelectedFakeCollectionToken] =
    useState<SimpleInfiniteListItem<WalletNft>>();
  const [selectedToken, setSelectedToken] =
    useState<SimpleInfiniteListItem<ContractNft>>();
  const {
    walletNfts,
    loadMore,
    hasMore,
    loading: loadingWalletNfts,
  } = usePaginatedWalletNfts();

  const collectionNetwork = useMemo(() => {
    let network = NetworkType.ETHEREUM;

    if (selectedFakeCollectionToken?.data?.chain === "polygon") {
      network = NetworkType.POLYGON;
    }
    return network;
  }, [selectedFakeCollectionToken]);

  const collectionAddress = useMemo(() => {
    return selectedFakeCollectionToken?.data?.contract || "";
  }, [selectedFakeCollectionToken]);

  const { data: collectionDetails, loading: loadingCollection } =
    useContractDetailsQuery({
      variables: {
        network: collectionNetwork,
        address: collectionAddress,
      },
      skip: selectedFakeCollectionToken?.data == null,
    });

  const mappedWalletNfts = useMemo<SimpleInfiniteListItem<WalletNft>[]>(() => {
    if (walletNfts == null) return [];

    return walletNfts.map((nft) => {
      const networkDetails = networkByNameLookup(
        nft.chain.toUpperCase() as NetworkType
      );

      return {
        key: `${nft.contract}-${nft.id}`,
        id: nft.id,
        title: nft.name,
        subtitle: networkDetails.displayName,
        imageUrl: nft.imageUrl ?? "",
        data: nft,
      };
    });
  }, [walletNfts]);

  const handleSelectCollection = () => {
    if (collectionDetails?.contractDetails == null) return;
    if (selectedToken?.data == null) return;

    const condition: Condition = {
      type: ConditionType.NFT,
      amount: 1,
      data: {
        network: collectionNetwork,
        address: selectedToken.data.contract || collectionAddress,
        name:
          (selectedToken.data.name || collectionDetails.contractDetails.name) ??
          "Unknown",
        collectionName:
          collectionDetails.contractDetails.name ?? "Unknown Collection",
        imageUrl:
          selectedToken.data.imageUrl ||
          collectionDetails.contractDetails.imageUrl,
        videoUrl: selectedToken.data.videoUrl,
        tokenId: selectedToken.data.id,
        standard: collectionDetails.contractDetails.standard,
      },
    };

    onSelectCollectionToken({
      network: collectionNetwork,
      contract: collectionAddress,
      token: undefined,
      condition,
    });
  };

  return (
    <>
      {step === 0 && (
        <Box>
          <ScrollArea.Autosize mih={100} maxHeight={"auto"}>
            <SimpleInfiniteGrid<WalletNft>
              items={mappedWalletNfts}
              hasMore={hasMore}
              loading={loadingWalletNfts}
              loadMore={loadMore}
              selectedItem={selectedFakeCollectionToken}
              onSelectItem={setSelectedFakeCollectionToken}
              loadingElement={<LoadingElement />}
              emptyResultsElement={
                <NoResultsElement text="No collections found in your wallet." />
              }
              gridCols={isMedium ? 3 : 1}
            />
          </ScrollArea.Autosize>

          <Space h="sm" />

          <BottomModalSection>
            <Button
              disabled={collectionDetails?.contractDetails == null}
              size="xl"
              fullWidth
              loading={loadingCollection}
              onClick={() => {
                setStep(1);
              }}
            >
              Select collection
            </Button>
          </BottomModalSection>
        </Box>
      )}
      {step === 1 && (
        <Box>
          <CollectionDetailsSection
            network={collectionNetwork}
            collection={collectionDetails}
            onClear={() => {
              setSelectedToken(undefined);
              setStep(0);
            }}
          />

          <Space h="sm" />

          {collectionDetails?.contractDetails != null && (
            <ContractCollectionTokenGrid
              contractCollection={collectionDetails.contractDetails}
              network={collectionNetwork}
              selectedToken={selectedToken}
              onSelectToken={setSelectedToken}
            />
          )}

          <BottomModalSection>
            <Button
              disabled={selectedToken?.data == null}
              size="xl"
              fullWidth
              loading={loadingCollection}
              onClick={handleSelectCollection}
            >
              {"Select token" +
                ((selectedToken?.data?.id &&
                  ` ( ${selectedToken?.data?.id} )`) ||
                  "")}
            </Button>
          </BottomModalSection>
        </Box>
      )}
    </>
  );
}

function WalletAttributeSelectionFlow(props: {
  onSelectCollectionAttribute: (data: SelectedWalletCondition) => void;
}) {
  const { onSelectCollectionAttribute } = props;
  const { isMedium } = useMantineMediaQueries();
  const [step, setStep] = useState(0);
  const [selectedFakeCollectionToken, setSelectedFakeCollectionToken] =
    useState<SimpleInfiniteListItem<WalletNft>>();
  const [selectedAttribute, setSelectedAttribute] =
    useState<TokenGridAttribute>();

  const {
    walletNfts,
    loadMore,
    hasMore,
    loading: loadingWalletNfts,
  } = usePaginatedWalletNfts();

  const collectionNetwork = useMemo(() => {
    let network = NetworkType.ETHEREUM;

    if (selectedFakeCollectionToken?.data?.chain === "polygon") {
      network = NetworkType.POLYGON;
    }
    return network;
  }, [selectedFakeCollectionToken]);

  const collectionAddress = useMemo(() => {
    return selectedFakeCollectionToken?.data?.contract || "";
  }, [selectedFakeCollectionToken]);

  const { data: collectionDetails, loading: loadingCollection } =
    useContractDetailsQuery({
      variables: {
        network: collectionNetwork,
        address: collectionAddress,
      },
      skip: selectedFakeCollectionToken?.data == null,
    });

  const mappedWalletNfts = useMemo<SimpleInfiniteListItem<WalletNft>[]>(() => {
    if (walletNfts == null) return [];

    return walletNfts.map((nft) => {
      const networkDetails = networkByNameLookup(
        nft.chain.toUpperCase() as NetworkType
      );

      return {
        key: `${nft.contract}-${nft.id}`,
        id: nft.id,
        title: nft.name,
        subtitle: networkDetails.displayName,
        imageUrl: nft.imageUrl ?? "",
        data: nft,
      };
    });
  }, [walletNfts]);

  const handleSelectAttribute = () => {
    if (collectionDetails?.contractDetails == null) return;
    if (selectedAttribute == null) return;

    const condition: Condition = {
      type: ConditionType.ATTRIBUTE,
      amount: 1,
      data: {
        network: collectionNetwork,
        address: collectionAddress,
        name: collectionDetails.contractDetails.name ?? "Unknown",
        collectionName:
          collectionDetails.contractDetails.name ?? "Unknown Collection",
        imageUrl: collectionDetails.contractDetails.imageUrl,
        standard: collectionDetails.contractDetails.standard,
        attributes: [
          {
            key: selectedAttribute.traitType,
            value: [selectedAttribute.traitValue],
          },
        ],
      },
    };

    onSelectCollectionAttribute({
      network: collectionNetwork,
      contract: collectionAddress,
      condition,
    });
  };

  return (
    <>
      {step === 0 && (
        <Box>
          <ScrollArea.Autosize mih={100} maxHeight={"auto"}>
            <SimpleInfiniteGrid<WalletNft>
              items={mappedWalletNfts}
              hasMore={hasMore}
              loading={loadingWalletNfts}
              loadMore={loadMore}
              selectedItem={selectedFakeCollectionToken}
              onSelectItem={setSelectedFakeCollectionToken}
              loadingElement={<LoadingElement />}
              emptyResultsElement={
                <NoResultsElement text="No collections found in your wallet." />
              }
              gridCols={isMedium ? 3 : 1}
            />
          </ScrollArea.Autosize>

          <Space h="sm" />

          <BottomModalSection>
            <Button
              disabled={collectionDetails?.contractDetails == null}
              size="xl"
              fullWidth
              loading={loadingCollection}
              onClick={() => {
                setStep(1);
              }}
            >
              Select collection
            </Button>
          </BottomModalSection>
        </Box>
      )}
      {step === 1 && (
        <Box>
          <CollectionDetailsSection
            network={collectionNetwork}
            collection={collectionDetails}
            onClear={() => {
              setSelectedAttribute(undefined);
              setStep(0);
            }}
          />

          <Space h="xl" />

          {collectionDetails?.contractDetails != null && (
            <ContractCollectionAttributeGrid
              network={collectionNetwork}
              contract={collectionAddress}
              selectedAttribute={selectedAttribute}
              onSelectAttribute={setSelectedAttribute}
            />
          )}

          <BottomModalSection>
            <Button
              disabled={selectedAttribute == null}
              size="xl"
              fullWidth
              loading={loadingCollection}
              onClick={handleSelectAttribute}
            >
              Select attribute
            </Button>
          </BottomModalSection>
        </Box>
      )}
    </>
  );
}

const usePaginatedWalletNfts = (props?: { skip?: boolean }) => {
  const { walletAddress } = useAuth();

  const [cursor, setCursor] = useState("");
  const pageSize = 6;

  const query = useWalletNftsQuery({
    variables: {
      address: walletAddress!,
      first: pageSize,
      after: cursor || null,
    },
    fetchPolicy: "cache-first",
    skip: props?.skip || walletAddress == null,
    notifyOnNetworkStatusChange: true,
    ...props,
  });

  const data = query.data?.walletNftsPaginated;
  const nextCursor = data?.pageInfo.endCursor;

  const loadMore = useCallback(() => {
    query.fetchMore({
      variables: {
        after: nextCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        fetchMoreResult.walletNftsPaginated.walletNfts = [
          ...(previousResult?.walletNftsPaginated?.walletNfts ?? []),
          ...(fetchMoreResult?.walletNftsPaginated?.walletNfts ?? []),
        ];
        return fetchMoreResult;
      },
    });
  }, [query.fetchMore, nextCursor]);

  const reset = () => {
    setCursor("");
  };

  return {
    ...query,
    walletNfts: data?.walletNfts || [],
    hasMore: data?.pageInfo.hasNextPage,
    loadMore,
    reset,
  };
};
